// src/services/pftMoveService.js
//
// The reads and writes behind moving a PFT Uploads card into Lung Center Referrals.
// Rules (tested) live in pftMovePlan.js and pftMergePlan.js. Every write is audited
// on the patient (patientAuditService) and on the imports (pft_imports/{id}/audit).
//
// Accuracy over speed:
//   * the match check runs again right before writing, so a patient added meanwhile is
//     never created twice;
//   * a merge re-reads the patient and refuses if it changed since the person looked
//     (they review again);
//   * if the Google Sheet (Dubois) can't be read, nothing is created: a Sheet patient
//     could otherwise be duplicated.

import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { getCollectionPath, getConsentsPath, getDuplicatesPath } from "./firestorePaths";
import { activeSiteUsesLegacySheet } from "./activeTenant";
import { fetchSheetPatients } from "./sheetPatientsService";
import { buildPatientDiff, getMrnLast4, logPatientAuditEvent, PATIENT_CLINICAL_FIELDS, PATIENT_PROFILE_FIELDS } from "./patientAuditService";
import { findCardMatches, newPatientRecord, planSignature, sheetMergeBody } from "./pftMovePlan";
import { applyMerge, incomingFromCard, planMerge } from "./pftMergePlan";
import { sheetSurveyAdditions, surveyMergeSummary, surveyMergeUpdates } from "./pftSurveyAnswers";
import { pftFieldsForSave, testsFromRecord } from "./pftTests";
import { authedFetch } from "./authedFetch";
import { FUNCTIONS_BASE } from "../config/functionsBase";
import { loadSurveySubmission } from "./pftSurveyService";

const me = () => (auth.currentUser && auth.currentUser.email) || "unknown";

export class MatchFoundError extends Error {
  constructor(matches) {
    super("This patient may already be in the system.");
    this.name = "MatchFoundError";
    this.matches = matches;
  }
}
export class PatientChangedError extends Error {
  constructor() {
    super("This patient's record changed since you opened the merge. Review it again.");
    this.name = "PatientChangedError";
  }
}

/**
 * Everyone who could be this patient: consents and (Dubois) the Sheet. Not `duplicates`
 * (decided 2026-10-20): those records were set aside as copies of another patient, so
 * PFT data is never merged into them.
 */
export const loadMatchCandidates = async () => {
  const out = [];
  const cons = await getDocs(collection(db, getConsentsPath()));
  cons.forEach((d) => out.push({ id: d.id, store: "consents", ...d.data() }));
  if (activeSiteUsesLegacySheet()) {
    let rows;
    try {
      rows = await fetchSheetPatients();
    } catch (e) {
      throw new Error("Couldn't check the Google Sheet patients, so nothing was moved. Try again in a moment.");
    }
    rows.forEach((p) => out.push({ ...p, id: p.token, store: "sheet", name: p.fullName || p.name || "" }));
  }
  return out;
};

const importsPath = () => getCollectionPath("pft_imports");

/** The card's health-survey answers when the patient answered (pftSurvey.js), else null. */
export const surveyForCard = async (card) => {
  const s = card && card.survey;
  if (!s || s.status !== "responded" || !s.submissionId) return null;
  try {
    return await loadSurveySubmission(s.submissionId);
  } catch (e) {
    throw new Error("Couldn't load this patient's health-survey answers, so nothing was moved. Try again.");
  }
};

const markImports = async (card, status, patientId, store, by) => {
  const batch = writeBatch(db);
  for (const imp of card.pendingImports) {
    batch.update(doc(db, importsPath(), imp.id), {
      status,
      patientId,
      patientStore: store,
      [`${status}By`]: by,
      [`${status}At`]: serverTimestamp(),
    });
  }
  await batch.commit();
  for (const imp of card.pendingImports) {
    try {
      await addDoc(collection(db, `${importsPath()}/${imp.id}/audit`), {
        action: status === "moved" ? "MOVED_TO_REFERRALS" : "MERGED_INTO_PATIENT",
        by,
        at: serverTimestamp(),
        patientId,
        patientStore: store,
      });
    } catch (e) {
      console.warn("pft import audit failed", e && e.code);
    }
  }
};

/**
 * No match: create the patient in Lung Center Referrals. Re-checks for a match first
 * and throws MatchFoundError if one appeared. Returns the new patient's id.
 */
export const createPatientFromCard = async (
  card,
  { diagnosis = [], allowMatches = false, uncheckedItems = 0 } = {},
  // "Move all" passes one list it loaded for the whole batch (and this adds each new
  // patient to it, so two cards for one person in a batch can't both be created).
  candidates = null,
) => {
  const by = me();
  const pool = candidates || (await loadMatchCandidates());
  const matches = findCardMatches(card, pool);
  // allowMatches: the person looked at the possible matches and said none is this patient.
  if (matches.length && !allowMatches) throw new MatchFoundError(matches);
  // A patient who answered the health survey comes with their answers and signed
  // consent. If those can't be read, nothing is created (they'd be silently lost).
  const survey = await surveyForCard(card);
  const record = newPatientRecord(card, { diagnosis, actor: by, survey });
  const ref = await addDoc(collection(db, getConsentsPath()), {
    ...record,
    // The Coordinator page lists patients ordered by signedAt (a record without it is
    // left out), and every other "add" flow sets it to when the patient was added.
    signedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
    movedToReferralsAt: serverTimestamp(),
  });
  await logPatientAuditEvent({
    patientId: ref.id,
    title: `Added to Lung Center Referrals from PFT Uploads (${card.pendingImports.length} PFT date${card.pendingImports.length === 1 ? "" : "s"})`,
    action: "PFT_IMPORT_PATIENT_CREATED",
    patientName: record.name,
    mrnLast4: getMrnLast4(record.mrn),
    source: "PftUpload",
    actorEmail: by,
    metadata: {
      pftImportIds: record.pftImportIds,
      pftDates: record.pftTests.map((t) => t.date),
      // Moved with items still unticked (allowed after a warning, decided 2026-10-06).
      ...(uncheckedItems ? { uncheckedItems } : {}),
      // Recorded when a person created this patient despite possible matches.
      ...(matches.length ? { createdDespiteMatches: matches.map((m) => `${m.store}/${m.record.id} (${m.how})`) } : {}),
      // Their health-survey answers, consent and contact details came along.
      ...(survey ? { surveyApplied: true, surveySubmissionId: survey.id } : {}),
    },
  });
  await markImports(card, "moved", ref.id, "consents", by);
  if (candidates) candidates.push({ id: ref.id, store: "consents", ...record });
  return ref.id;
};

// ---- Google Sheet patients (Dubois) ------------------------------------------------
// The row is read fresh; the merge is planned exactly like for Firestore patients; and
// only the CHANGES go to the server (mergePftIntoSheetPatient), each with the value the
// person saw, so the server can refuse if the row changed meanwhile.

const sheetRecord = async (token) => {
  const rows = await fetchSheetPatients();
  const row = rows.find((r) => r.token === token);
  if (!row) throw new Error("That patient is no longer in the Google Sheet.");
  return { ...row, name: row.fullName || row.name || "" };
};

const mergeIntoSheetPatient = async (card, match, choices, signature, uncheckedItems, survey = null) => {
  const by = me();
  const record = await sheetRecord(match.record.id);
  const plan = planMerge(record, incomingFromCard(card, survey));
  if (planSignature(plan) !== signature) throw new PatientChangedError();
  const result = applyMerge(record, plan, choices, { by });
  // A patient who answered the health survey: the server only ADDS these (diagnoses,
  // notes, consent link if empty, Other files, BMI if empty), never replaces.
  const additions = survey ? sheetSurveyAdditions(record, survey, plan, result.profileUpdates, { by }) : null;
  const body = sheetMergeBody(record, result, additions);
  const res = await authedFetch(`${FUNCTIONS_BASE}/mergePftIntoSheetPatient`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token: match.record.id, ...body }),
  });
  let reply = {};
  try {
    reply = await res.json();
  } catch {
    reply = {};
  }
  if (res.status === 409) throw new PatientChangedError();
  if (!res.ok) throw new Error(reply.error || "Couldn't update the Google Sheet patient.");
  await logPatientAuditEvent({
    patientId: match.record.id,
    title: `PFT data merged from PFT Uploads (${result.addedTests} new PFT date${result.addedTests === 1 ? "" : "s"})`,
    action: "PFT_IMPORT_MERGED",
    patientName: record.name,
    mrnLast4: getMrnLast4(record.mrn),
    source: "PftUpload",
    actorEmail: by,
    changes: body.changes.map(({ field, from, to }) => ({ field, from: from ?? null, to: to ?? null })),
    metadata: {
      pftImportIds: card.pendingImports.map((i) => i.id),
      addedTests: result.addedTests,
      updatedTests: result.updatedTests,
      keptConflicts: result.keptConflicts,
      recordPath: `sheet/${match.record.id}`,
      ...(uncheckedItems ? { uncheckedItems } : {}),
      ...(survey ? { surveyApplied: true, surveySubmissionId: survey.id, surveyFields: Object.keys(additions) } : {}),
      ...(result.decidedSoftChecks.length ? { softChecksSettled: result.decidedSoftChecks.map((c) => c.key) } : {}),
    },
  });
  await markImports(card, "merged", match.record.id, "sheet", by);
  return result;
};

/** The merge plan for one match, from a FRESH read (Firestore or Sheet patient). */
export const loadMergePlan = async (card, match) => {
  const survey = await surveyForCard(card);
  if (match.store === "sheet") {
    const record = await sheetRecord(match.record.id);
    const plan = planMerge(record, incomingFromCard(card, survey));
    if (survey) plan.fromSurvey = surveyMergeSummary(record, survey);
    return { record, plan, signature: planSignature(plan), ...(survey ? { survey } : {}) };
  }
  const path = match.store === "duplicates" ? getDuplicatesPath() : getConsentsPath();
  const snap = await getDoc(doc(db, path, match.record.id));
  if (!snap.exists()) throw new Error("That patient record no longer exists.");
  const record = snap.data();
  const plan = planMerge(record, incomingFromCard(card, survey));
  // What else the survey brings, for the merge screen (not part of the signature).
  if (survey) plan.fromSurvey = surveyMergeSummary(record, survey);
  return { record, plan, signature: planSignature(plan), ...(survey ? { survey } : {}) };
};

/**
 * Merge a card into an existing Firestore patient. `choices` as in applyMerge. Refuses
 * (PatientChangedError) if the patient changed since `signature` was taken. The
 * patient's status is never changed. Returns { addedTests, updatedTests, profileUpdates }.
 */
export const mergeIntoFirestorePatient = async (card, match, choices, signature, { uncheckedItems = 0 } = {}) => {
  const survey = await surveyForCard(card);
  if (match.store === "sheet") return mergeIntoSheetPatient(card, match, choices, signature, uncheckedItems, survey);
  const by = me();
  const path = match.store === "duplicates" ? getDuplicatesPath() : getConsentsPath();
  const ref = doc(db, path, match.record.id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("That patient record no longer exists.");
  const fresh = snap.data();
  const plan = planMerge(fresh, incomingFromCard(card, survey));
  if (planSignature(plan) !== signature) throw new PatientChangedError();
  const result = applyMerge(fresh, plan, choices, { by });
  // A patient who answered the health survey: diagnosis, consent, Other files, notes.
  const surveyUpdates = survey ? surveyMergeUpdates(fresh, survey, plan, result.profileUpdates, { by }) : {};
  const update = {
    ...result.profileUpdates,
    ...surveyUpdates,
    ...pftFieldsForSave({ stored: fresh, base: testsFromRecord(fresh), local: result.tests }),
    lastEditedBy: by,
    lastEditedAt: serverTimestamp(),
  };
  await updateDoc(ref, update);
  const fields = [...PATIENT_PROFILE_FIELDS, ...PATIENT_CLINICAL_FIELDS, "mrn"];
  await logPatientAuditEvent({
    patientId: match.record.id,
    title: `PFT data merged from PFT Uploads (${result.addedTests} new PFT date${result.addedTests === 1 ? "" : "s"})`,
    action: "PFT_IMPORT_MERGED",
    patientName: fresh.name || fresh.fullName || "",
    mrnLast4: getMrnLast4(update.mrn || fresh.mrn),
    source: "PftUpload",
    actorEmail: by,
    changes: buildPatientDiff(fresh, { ...fresh, ...update }, fields),
    metadata: {
      pftImportIds: card.pendingImports.map((i) => i.id),
      addedTests: result.addedTests,
      updatedTests: result.updatedTests,
      keptConflicts: result.keptConflicts,
      ...(uncheckedItems ? { uncheckedItems } : {}),
      ...(result.decidedSoftChecks.length ? { softChecksSettled: result.decidedSoftChecks.map((c) => c.key) } : {}),
      ...(survey ? { surveyApplied: true, surveySubmissionId: survey.id, surveyFields: Object.keys(surveyUpdates) } : {}),
      recordPath: `${match.store}/${match.record.id}`,
    },
  });
  await markImports(card, "merged", match.record.id, match.store, by);
  return result;
};
